import {
  v1AreaTypeControllerCreate,
  v1AreaTypeControllerDelete,
  v1AreaTypeControllerList,
  v1AreaTypeControllerRead,
  v1AreaTypeControllerUpdate,
  v1DefinitionSetControllerCreate,
  v1DefinitionSetControllerDelete,
  v1DefinitionSetControllerList,
  v1DefinitionSetControllerUpdate,
  v1RiskTypeControllerCreate,
  v1RiskTypeControllerList,
  v1RiskValueChainControllerCreate,
  v1RiskValueChainControllerCreateAreaTypeRecommendation,
  v1RiskValueChainControllerCreateRiskTypeRecommendation,
  v1RiskValueChainControllerCreateScenarioTypeRecommendation,
  v1RiskValueChainControllerCreateSolutionTypeRecommendation,
  v1RiskValueChainControllerDelete,
  v1RiskValueChainControllerDeleteAreaTypeRecommendation,
  v1RiskValueChainControllerDeleteRiskTypeRecommendation,
  v1RiskValueChainControllerDeleteScenarioTypeRecommendation,
  v1RiskValueChainControllerDeleteSolutionTypeRecommendation,
  v1RiskValueChainControllerDuplicate,
  v1RiskValueChainControllerGetRiskTypeRecommendations,
  v1RiskValueChainControllerGetAllRiskTypeRecommendations,
  v1RiskValueChainControllerRead,
  v1RiskValueChainControllerUpdate,
  v1ScenarioTypeControllerCreate,
  v1ScenarioTypeControllerDelete,
  v1ScenarioTypeControllerList,
  v1ScenarioTypeControllerRead,
  v1ScenarioTypeControllerUpdate,
  v1SolutionTypeControllerCreate,
  v1SolutionTypeControllerDelete,
  v1SolutionTypeControllerUpdate,
  v1UserControllerGetAllRoles,
  v1UserControllerGetAllUsers,
  v1UserControllerUpdateUser,
  v1WorkspacesControllerAddAdministrator,
  v1WorkspacesControllerCreate,
  v1WorkspacesControllerDelete,
  v1WorkspacesControllerFindAll,
  v1WorkspacesControllerFindAllOverview,
  v1WorkspacesControllerGetWorkspaceAssociatedSolutionTypes,
  v1WorkspacesControllerGetWorkspaceAvailableRiskValueChains,
  v1WorkspacesControllerGetWorkspaceAvailableSolutionTypes,
  v1WorkspacesControllerRemoveAdministrator,
  v1WorkspacesControllerUpdate,
  v1WorkspacesControllerUpdateMember,
  v1WorkspacesControllerUpdateRiskValueChainAssociations,
  v1WorkspacesControllerUpdateSolutionTypeAssociations,
} from '~/generated/api'
import type {
  CreateSolutionTypeRequest,
  CreateDefinitionSetRequest,
  CreateRiskValueChainRequest,
  UpdateDefinitionSetRequest,
  UpdateRiskValueChainRequest,
  WorkspaceSummaryResponse,
} from '~/generated/api'
import type { UpdateUserRequest } from '~/generated/api/types.gen'
import type {
  CreateWorkspaceRequest,
  DefinitionSet,
  AreaType,
  ManagedUser,
  RiskType,
  RiskTypeRecommendation,
  RiskValueChain,
  ScenarioType,
  SolutionType,
  UserRole,
  Workspace,
} from '~/models'
import { apiErrorDetails, apiErrorMessageKey } from '~/utils/apiError'
import { UserRolesEnum } from '~/utils/enums/userRoles'
import { watch } from 'vue'
import {
  GLOBAL_WORKSPACE_ID,
  toWorkspacePathId,
} from '~/utils/workspacePathId'
import { normalizeSolutionType } from '~/models'

/**
 * A row of the workspace overview (`overview: true`): id, name and timestamps
 * only. The synthetic Global entry appended for global admins has no id.
 */
export type WorkspaceOverview =
  | WorkspaceSummaryResponse
  | { id?: undefined, name: string }

/**
 * A workspace the admin UI can select: a full workspace, or an overview row
 * picked from the header or the selection modal. Consumers read only `id`
 * and `name`.
 */
export type SelectableWorkspace = Workspace | WorkspaceOverview

export type WorkspaceScopeInput = string | { workspaceId?: string }

export function resolveWorkspaceScope(
  scope?: WorkspaceScopeInput,
  activeWorkspaceId?: string,
): string | undefined {
  if (scope !== null && typeof scope === 'object') {
    return scope.workspaceId
  }
  if (typeof scope === 'string') {
    return scope
  }
  return activeWorkspaceId
}

export const useAdminStore = defineStore(
  'admin',
  () => {
    const projectStore = useProjectStore()
    const { getBatchSolutionImages, clearCache } = projectStore
    const { clearAssessmentCache } = useAssessmentStore()
    const { showNotification, customError } = storeToRefs(projectStore)
    const { userRoles } = storeToRefs(useAuthStore())
    const appConfig = useAppConfig()
    const adminApiHeaders = {
      'admin-api-key': appConfig.ADVISOR_ADMIN_API_KEY,
    }

    async function withLoading<T>(
      loading: Ref<boolean>,
      operation: () => Promise<T>,
    ) {
      loading.value = true
      try {
        return await operation()
      }
      catch (requestError) {
        // Callers handle failures themselves, but none of them can tell the
        // user that the gateway firewall may have rejected the text.
        if (apiErrorDetails(requestError).code === 'write_unanswered') {
          customError.value = apiErrorMessageKey(apiErrorDetails(requestError))
          showNotification.value = true
        }
        throw requestError
      }
      finally {
        loading.value = false
      }
    }

    const riskValueChains = ref<RiskValueChain[]>([])
    const globalRiskValueChains = ref<RiskValueChain[]>([])
    const allAreaTypes = ref<AreaType[]>([])
    // definition sets
    const definitionSets = ref<DefinitionSet[]>([])
    const selectedRiskValueChain = ref<RiskValueChain | null>(null)
    const selectedAreaType = ref<AreaType | null>(null)
    const selectedScenario = ref<ScenarioType | null>(null)
    const allScenarioTypes = ref<ScenarioType[]>([])
    const allRiskTypes = ref<RiskType[]>([])
    const managedUsers = ref<ManagedUser[]>([])
    const allRoles = ref<UserRole[]>([])
    const allAdministeredWorkspaces = ref<Workspace[]>([])
    const selectedWorkspace = ref<SelectableWorkspace | null>(null)
    const selectedRiskTypeRecommendation = ref<RiskTypeRecommendation | null>(
      null,
    )
    const availableSolutionTypes = ref<Record<string, SolutionType[]>>({})
    const associatedSolutionTypes = ref<Record<string, SolutionType[]>>({})
    const riskTypeRecommendationsCache = new Map<
      string,
      { data: RiskTypeRecommendation[], timestamp: number }
    >()
    const riskTypeRecommendationsCacheDuration = 10 * 60 * 1000

    const shouldRefresh = ref(false)
    let latestAreaTypesRequestId = 0
    const areaTypesLoadingCounts = new WeakMap<Ref<boolean>, number>()

    let latestDefinitionSetsRequestId = 0
    const definitionSetsLoadingCounts = new WeakMap<Ref<boolean>, number>()

    let latestRiskValueChainsRequestId = 0
    const riskValueChainsLoadingCounts = new WeakMap<Ref<boolean>, number>()

    let latestScenarioTypesRequestId = 0
    const scenarioTypesLoadingCounts = new WeakMap<Ref<boolean>, number>()

    function trackRequest(
      loadingCounts: WeakMap<Ref<boolean>, number>,
      fetching: Ref<boolean>,
      nextRequestId: () => number,
    ) {
      const requestId = nextRequestId()
      const pendingRequests = (loadingCounts.get(fetching) ?? 0) + 1
      loadingCounts.set(fetching, pendingRequests)
      fetching.value = true

      const finish = () => {
        const remaining = (loadingCounts.get(fetching) ?? 1) - 1
        if (remaining <= 0) {
          loadingCounts.delete(fetching)
          fetching.value = false
        }
        else {
          loadingCounts.set(fetching, remaining)
        }
      }

      return { requestId, finish }
    }

    watch(
      () => toWorkspacePathId(selectedWorkspace.value?.id),
      () => {
        allAreaTypes.value = []
        riskValueChains.value = []
        definitionSets.value = []
        allScenarioTypes.value = []
      },
    )

    async function createRiskValueChain(
      posting: Ref<boolean>,
      body: CreateRiskValueChainRequest,
      areaTypes: AreaType[],
      scope?: WorkspaceScopeInput,
    ) {
      const capturedWorkspaceId = resolveWorkspaceScope(scope, selectedWorkspace.value?.id)
      const capturedScope = { workspaceId: capturedWorkspaceId }
      const { data: responseData } = await withLoading(posting, () =>
        v1RiskValueChainControllerCreate({
          body: { ...body, workspaceId: capturedWorkspaceId },
          headers: adminApiHeaders,
          throwOnError: true,
        }))
      const data = responseData as unknown as RiskValueChain
      if (data) {
        // link area types to created risk value chain with explicitly captured scope
        await linkAreaTypesToRiskValueChain(posting, data.id, areaTypes, capturedScope)
      }
      // fetch new risk value chains with explicitly captured scope
      await fetchWorkspaceAvailableRiskValueChains(posting, capturedScope)
      return data as unknown as RiskValueChain
    }

    // update risk value chain
    async function updateRiskValueChain(
      posting: Ref<boolean>,
      id: string,
      body: UpdateRiskValueChainRequest,
    ) {
      const { data } = await withLoading(posting, () =>
        v1RiskValueChainControllerUpdate({
          body,
          path: { id },
          throwOnError: true,
        }))
      return data as unknown as RiskValueChain
    }

    async function fetchAllAreaTypes(
      fetching: Ref<boolean>,
      scope?: WorkspaceScopeInput,
    ) {
      const targetWorkspaceId = resolveWorkspaceScope(scope, selectedWorkspace.value?.id)
      const requestedScope = toWorkspacePathId(targetWorkspaceId)
      const { requestId, finish } = trackRequest(
        areaTypesLoadingCounts,
        fetching,
        () => ++latestAreaTypesRequestId,
      )

      try {
        const query = { workspaceId: targetWorkspaceId }
        const { data } = await v1AreaTypeControllerList({
          query,
          throwOnError: true,
        })
        if (
          requestId === latestAreaTypesRequestId
          && requestedScope === toWorkspacePathId(selectedWorkspace.value?.id)
        ) {
          allAreaTypes.value = data?.areaTypes ?? []
        }
      }
      finally {
        finish()
      }
    }

    async function fetchDefinitionSets(
      fetching: Ref<boolean>,
      scope?: WorkspaceScopeInput,
    ) {
      const targetWorkspaceId = resolveWorkspaceScope(scope, selectedWorkspace.value?.id)
      const requestedScope = toWorkspacePathId(targetWorkspaceId)
      const { requestId, finish } = trackRequest(
        definitionSetsLoadingCounts,
        fetching,
        () => ++latestDefinitionSetsRequestId,
      )

      try {
        const query = {
          workspaceId: targetWorkspaceId,
        }
        const { data } = await v1DefinitionSetControllerList({
          query,
          throwOnError: true,
        })
        if (
          requestId === latestDefinitionSetsRequestId
          && requestedScope === toWorkspacePathId(selectedWorkspace.value?.id)
        ) {
          definitionSets.value = data?.definitionSets ?? []
        }
      }
      finally {
        finish()
      }
    }

    async function createAreaType(
      posting: Ref<boolean>,
      areaTypeNames: string[],
      workspaceId?: string,
    ) {
      return await withLoading(posting, async () =>
        Promise.all(
          areaTypeNames.map(async (name) => {
            const { data } = await v1AreaTypeControllerCreate({
              body: { name, isCustom: false, workspaceId },
              headers: adminApiHeaders,
              throwOnError: true,
            })
            return data
          }),
        ))
    }

    // create definition set
    async function createDefinitionSet(
      posting: Ref<boolean>,
      body: CreateDefinitionSetRequest,
    ) {
      const { data } = await withLoading(posting, () =>
        v1DefinitionSetControllerCreate({
          body,
          headers: adminApiHeaders,
          throwOnError: true,
        }))
      return data
    }

    async function updateDefinitionSet(
      posting: Ref<boolean>,
      id: string,
      body: UpdateDefinitionSetRequest,
    ) {
      const { data } = await withLoading(posting, () =>
        v1DefinitionSetControllerUpdate({
          body,
          path: { id },
          throwOnError: true,
        }))
      return data
    }

    async function deleteDefinitionSet(deleting: Ref<boolean>, id: string) {
      const { data } = await withLoading(deleting, () =>
        v1DefinitionSetControllerDelete({
          headers: adminApiHeaders,
          path: { id },
          throwOnError: true,
        }))
      return data
    }

    // link area types to created risk value chain
    async function linkAreaTypesToRiskValueChain(
      posting: Ref<boolean>,
      id: string,
      areaTypes: AreaType[],
      scope?: WorkspaceScopeInput,
    ) {
      if (id && areaTypes?.length) {
        const targetWorkspaceId = resolveWorkspaceScope(scope, selectedWorkspace.value?.id)
        const capturedScope = { workspaceId: targetWorkspaceId }
        await withLoading(posting, async () => {
          await Promise.all(
            areaTypes
              .filter(areaType => areaType.id)
              .map(async (areaType) => {
                const { data } = await v1RiskValueChainControllerCreateAreaTypeRecommendation({
                  body: { areaTypeId: areaType.id! },
                  headers: adminApiHeaders,
                  path: { id },
                  throwOnError: true,
                })
                return data
              }),
          )
          const createdAreaTypes = await Promise.all(
            areaTypes
              .filter(areaType => !areaType.id)
              .map(async (areaType) => {
                const { data } = await v1AreaTypeControllerCreate({
                  body: {
                    name: areaType.name,
                    isCustom: false,
                    workspaceId: targetWorkspaceId,
                  },
                  headers: adminApiHeaders,
                  throwOnError: true,
                })
                return data
              }),
          )
          await Promise.all(
            createdAreaTypes.map(async (areaType) => {
              const { data } = await v1RiskValueChainControllerCreateAreaTypeRecommendation({
                body: { areaTypeId: areaType.id! },
                headers: adminApiHeaders,
                path: { id },
                throwOnError: true,
              })
              return data
            }),
          )
          if (createdAreaTypes.length > 0) {
            await fetchAllAreaTypes(posting, capturedScope)
          }
        })
      }
    }

    async function unlinkAreaTypesFromRiskValueChain(
      posting: Ref<boolean>,
      id: string,
      areaTypes: AreaType[],
    ) {
      // @todo wait for backend implementation
      if (id && areaTypes?.length) {
        await withLoading(posting, () =>
          Promise.all(
            areaTypes.map(async (areaType) => {
              const { data } = await v1RiskValueChainControllerDeleteAreaTypeRecommendation({
                headers: adminApiHeaders,
                path: { id, areaTypeId: areaType.id! },
                throwOnError: true,
              })
              return data
            }),
          ))
      }
    }

    async function linkRiskTypesToAreaType(
      posting: Ref<boolean>,
      riskValueChainId: string,
      areaTypeId: string,
      riskTypes: RiskType[],
    ) {
      if (riskValueChainId && areaTypeId && riskTypes?.length) {
        await withLoading(posting, () =>
          Promise.all(
            riskTypes.map(async (riskType) => {
              const { data } = await v1RiskValueChainControllerCreateRiskTypeRecommendation({
                body: { riskTypeId: riskType.id! },
                headers: adminApiHeaders,
                path: { id: riskValueChainId, areaTypeId },
                throwOnError: true,
              })
              return data
            }),
          ))
      }
    }

    async function unlinkRiskTypesFromAreaType(
      deleting: Ref<boolean>,
      riskValueChainId: string,
      areaTypeId: string,
      riskTypes: RiskType[],
    ) {
      if (riskValueChainId && areaTypeId && riskTypes?.length) {
        await withLoading(deleting, () =>
          Promise.all(
            riskTypes.map(async (riskType) => {
              const { data } = await v1RiskValueChainControllerDeleteRiskTypeRecommendation({
                headers: adminApiHeaders,
                path: {
                  id: riskValueChainId,
                  areaTypeId,
                  riskTypeId: riskType.id,
                },
                throwOnError: true,
              })
              return data
            }),
          ))
      }
    }

    async function linkScenariosToRiskType(
      posting: Ref<boolean>,
      riskValueChainId: string,
      areaTypeId: string,
      riskTypeId: string,
      scenarios: ScenarioType[],
    ) {
      if (riskValueChainId && areaTypeId && riskTypeId && scenarios?.length) {
        await withLoading(posting, () =>
          Promise.all(
            scenarios.map(async (scenario) => {
              const { data } = await v1RiskValueChainControllerCreateScenarioTypeRecommendation({
                body: { scenarioTypeId: scenario.id },
                headers: adminApiHeaders,
                path: { id: riskValueChainId, areaTypeId, riskTypeId },
                throwOnError: true,
              })
              return data
            }),
          ))
      }
    }

    async function unlinkScenariosFromRiskType(
      deleting: Ref<boolean>,
      riskValueChainId: string,
      areaTypeId: string,
      riskTypeId: string,
      scenarios: ScenarioType[],
    ) {
      if (riskValueChainId && areaTypeId && riskTypeId && scenarios?.length) {
        await withLoading(deleting, () =>
          Promise.all(
            scenarios.map(async (scenario) => {
              const { data } = await v1RiskValueChainControllerDeleteScenarioTypeRecommendation({
                headers: adminApiHeaders,
                path: {
                  id: riskValueChainId,
                  areaTypeId,
                  riskTypeId,
                  scenarioTypeId: scenario.id,
                },
                throwOnError: true,
              })
              return data
            }),
          ))
      }
    }

    async function linkSolutionsToScenario(
      posting: Ref<boolean>,
      riskValueChainId: string,
      areaTypeId: string,
      riskTypeId: string,
      scenarioId: string,
      solutions: SolutionType[],
    ) {
      if (
        riskValueChainId
        && areaTypeId
        && riskTypeId
        && scenarioId
        && solutions?.length
      ) {
        await withLoading(posting, () =>
          Promise.all(
            solutions.map(async (solution) => {
              const { data } = await v1RiskValueChainControllerCreateSolutionTypeRecommendation({
                body: { solutionTypeId: solution.id },
                headers: adminApiHeaders,
                path: {
                  id: riskValueChainId,
                  areaTypeId,
                  riskTypeId,
                  scenarioTypeId: scenarioId,
                },
                throwOnError: true,
              })
              return data
            }),
          ))
      }
    }

    async function unlinkSolutionsFromScenario(
      deleting: Ref<boolean>,
      riskValueChainId: string,
      areaTypeId: string,
      riskTypeId: string,
      scenarioId: string,
      solutions: SolutionType[],
    ) {
      if (riskValueChainId && areaTypeId && riskTypeId && solutions?.length) {
        await withLoading(deleting, () =>
          Promise.all(
            solutions.map(async (solution) => {
              const { data } = await v1RiskValueChainControllerDeleteSolutionTypeRecommendation({
                headers: adminApiHeaders,
                path: {
                  id: riskValueChainId,
                  areaTypeId,
                  riskTypeId,
                  scenarioTypeId: scenarioId,
                  solutionTypeId: solution.id,
                },
                throwOnError: true,
              })
              return data
            }),
          ))
      }
    }

    // fetch workspace available risk value chains
    async function fetchWorkspaceAvailableRiskValueChains(
      fetching: Ref<boolean>,
      scope?: WorkspaceScopeInput,
    ) {
      const targetWorkspaceId = resolveWorkspaceScope(scope, selectedWorkspace.value?.id)
      if (!selectedWorkspace.value && targetWorkspaceId === undefined && scope === undefined) {
        return
      }
      const requestedScope = toWorkspacePathId(targetWorkspaceId)
      const { requestId, finish } = trackRequest(
        riskValueChainsLoadingCounts,
        fetching,
        () => ++latestRiskValueChainsRequestId,
      )

      try {
        const { data } = await v1WorkspacesControllerGetWorkspaceAvailableRiskValueChains({
          path: { id: requestedScope },
          throwOnError: true,
        })
        if (
          requestId === latestRiskValueChainsRequestId
          && requestedScope === toWorkspacePathId(selectedWorkspace.value?.id)
        ) {
          riskValueChains.value = (data?.riskValueChains as unknown as RiskValueChain[] ?? []).sort((a, b) =>
            b.createdAt.localeCompare(a.createdAt),
          )
        }
      }
      finally {
        finish()
      }
    }

    async function fetchGlobalRiskValueChains(fetching: Ref<boolean>) {
      const { data } = await withLoading(fetching, () =>
        v1WorkspacesControllerGetWorkspaceAvailableRiskValueChains({
          path: { id: GLOBAL_WORKSPACE_ID },
          throwOnError: true,
        }))
      if (data) {
        globalRiskValueChains.value = (data.riskValueChains as unknown as RiskValueChain[])
          .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
      }

      return globalRiskValueChains.value
    }

    async function fetchRiskValueChain(fetching: Ref<boolean>, id: string) {
      const { data } = await withLoading(fetching, () =>
        v1RiskValueChainControllerRead({ path: { id }, throwOnError: true }))
      selectedRiskValueChain.value = data as unknown as RiskValueChain
    }

    async function deleteRiskValueChain(deleting: Ref<boolean>, id: string) {
      if (id) {
        await withLoading(deleting, async () => {
          const { data } = await v1RiskValueChainControllerDelete({
            headers: adminApiHeaders,
            path: { id },
            throwOnError: true,
          })
          return data
        })
        riskValueChains.value = riskValueChains.value.filter(
          riskValueChain => riskValueChain.id !== id,
        )
        showNotification.value = true
      }
    }

    async function fetchAllRiskTypesRecommendations(
      fetching: Ref<boolean>,
      riskValueChainId: string,
    ): Promise<Record<string, RiskTypeRecommendation[]>> {
      const { data } = await withLoading(fetching, () =>
        v1RiskValueChainControllerGetAllRiskTypeRecommendations({
          path: { id: riskValueChainId },
          throwOnError: true,
        }))
      const result: Record<string, RiskTypeRecommendation[]> = {}
      const timestamp = Date.now()
      for (const area of data.areas) {
        const recommendations = area.recommendations as unknown as RiskTypeRecommendation[]
        result[area.areaTypeId] = recommendations
        riskTypeRecommendationsCache.set(`${riskValueChainId}:${area.areaTypeId}`, {
          data: recommendations,
          timestamp,
        })
      }
      return result
    }

    async function fetchRiskTypesRecommendations(
      fetching: Ref<boolean>,
      riskValueChainId: string,
      areaTypeId: string,
      refresh: boolean = false,
    ): Promise<RiskTypeRecommendation[] | null> {
      if (riskValueChainId && areaTypeId) {
        const cacheKey = `${riskValueChainId}:${areaTypeId}`
        const now = Date.now()
        if (refresh)
          riskTypeRecommendationsCache.delete(cacheKey)

        const cached = riskTypeRecommendationsCache.get(cacheKey)
        if (cached && now - cached.timestamp < riskTypeRecommendationsCacheDuration)
          return cached.data

        const { data } = await withLoading(fetching, () =>
          v1RiskValueChainControllerGetRiskTypeRecommendations({
            path: { id: riskValueChainId, areaTypeId },
            throwOnError: true,
          }))
        const recommendations = data.recommendations as unknown as RiskTypeRecommendation[]
        riskTypeRecommendationsCache.set(cacheKey, {
          data: recommendations,
          timestamp: now,
        })
        return recommendations
      }
      return null
    }

    // create new solution type
    async function createSolutionType(
      posting: Ref<boolean>,
      body: CreateSolutionTypeRequest,
    ): Promise<SolutionType> {
      const { data } = await withLoading(posting, () =>
        v1SolutionTypeControllerCreate({
          body,
          headers: adminApiHeaders,
          throwOnError: true,
        }))
      const result = normalizeSolutionType(data)
      clearCache()
      clearAssessmentCache()
      return result
    }

    async function deleteSolutionType(deleting: Ref<boolean>, id: string) {
      if (id) {
        const response = await withLoading(deleting, () =>
          v1SolutionTypeControllerDelete({
            headers: adminApiHeaders,
            path: { id },
            throwOnError: false,
          }))
        if (response.error !== undefined) {
          if (response.response?.status === 409)
            return undefined
          throw response.error
        }
        clearCache()
        clearAssessmentCache()
        return response.data
      }
    }

    async function updateSolutionType(
      posting: Ref<boolean>,
      id: string,
      body: CreateSolutionTypeRequest,
    ): Promise<SolutionType> {
      const { data } = await withLoading(posting, () =>
        v1SolutionTypeControllerUpdate({
          body,
          path: { id },
          throwOnError: true,
        }))
      const result = normalizeSolutionType(data)
      clearCache()
      clearAssessmentCache()
      return result
    }

    async function fetchAreaType(fetching: Ref<boolean>, id: string) {
      const { data } = await withLoading(fetching, () =>
        v1AreaTypeControllerRead({ path: { id }, throwOnError: true }))
      selectedAreaType.value = data
    }

    async function fetchScenario(fetching: Ref<boolean>, id: string) {
      const { data } = await withLoading(fetching, () =>
        v1ScenarioTypeControllerRead({ path: { id }, throwOnError: true }))
      selectedScenario.value = data
    }

    async function fetchAllScenarioTypes(
      fetching: Ref<boolean>,
      scope?: WorkspaceScopeInput,
    ) {
      const targetWorkspaceId = resolveWorkspaceScope(scope, selectedWorkspace.value?.id)
      const requestedScope = toWorkspacePathId(targetWorkspaceId)
      const { requestId, finish } = trackRequest(
        scenarioTypesLoadingCounts,
        fetching,
        () => ++latestScenarioTypesRequestId,
      )

      try {
        const query = {
          workspaceId: targetWorkspaceId,
        }
        const { data } = await v1ScenarioTypeControllerList({
          query,
          throwOnError: true,
        })
        if (
          requestId === latestScenarioTypesRequestId
          && requestedScope === toWorkspacePathId(selectedWorkspace.value?.id)
        ) {
          allScenarioTypes.value = data?.scenarioTypes ?? []
        }
      }
      finally {
        finish()
      }
    }

    async function deleteAreaType(deleting: Ref<boolean>, id: string) {
      if (id) {
        await withLoading(deleting, async () => {
          const { data } = await v1AreaTypeControllerDelete({
            headers: adminApiHeaders,
            path: { id },
            throwOnError: true,
          })
          return data
        })
        allAreaTypes.value = allAreaTypes.value.filter(
          areaType => areaType.id !== id,
        )
        showNotification.value = true
      }
    }

    async function fetchAllRiskTypes(fetching: Ref<boolean>) {
      const { data } = await withLoading(fetching, () =>
        v1RiskTypeControllerList({ throwOnError: true }))
      allRiskTypes.value = data?.riskTypes ?? []
    }

    async function createRiskType(posting: Ref<boolean>, name: string) {
      const { data: createdRiskType } = await withLoading(posting, () =>
        v1RiskTypeControllerCreate({
          body: { name },
          headers: adminApiHeaders,
          throwOnError: true,
        }))
      if (createdRiskType)
        allRiskTypes.value.push(createdRiskType)
    }

    async function createScenarioType(
      posting: Ref<boolean>,
      name: string,
      workspaceId?: string,
    ) {
      const { data: createdScenarioType } = await withLoading(posting, () =>
        v1ScenarioTypeControllerCreate({
          body: { name, workspaceId },
          headers: adminApiHeaders,
          throwOnError: true,
        }))
      if (createdScenarioType)
        allScenarioTypes.value.push(createdScenarioType)
      return createdScenarioType
    }

    async function deleteScenarioType(deleting: Ref<boolean>, id: string) {
      if (id) {
        await withLoading(deleting, async () => {
          const { data } = await v1ScenarioTypeControllerDelete({
            headers: adminApiHeaders,
            path: { id },
            throwOnError: true,
          })
          return data
        })
        allScenarioTypes.value = allScenarioTypes.value.filter(
          scenarioType => scenarioType.id !== id,
        )
        showNotification.value = true
      }
    }

    async function updateScenarioType(
      posting: Ref<boolean>,
      id: string,
      body: { name: string },
    ) {
      const { data } = await withLoading(posting, () =>
        v1ScenarioTypeControllerUpdate({
          body,
          path: { id },
          throwOnError: true,
        }))
      return data
    }

    async function fetchUsers(fetching: Ref<boolean>) {
      const { data } = await withLoading(fetching, () =>
        v1UserControllerGetAllUsers({ throwOnError: true }))
      managedUsers.value = (data?.users ?? []) as unknown as ManagedUser[]
    }

    async function updateUserAccess(
      posting: Ref<boolean>,
      userId: string,
      body: UpdateUserRequest,
    ) {
      const { data } = await withLoading(posting, () =>
        v1UserControllerUpdateUser({
          body,
          path: { userId },
          throwOnError: true,
        }))
      return data as unknown as ManagedUser
    }

    async function fetchAllRoles(fetching: Ref<boolean>) {
      const { data } = await withLoading(fetching, () =>
        v1UserControllerGetAllRoles({ throwOnError: true }))
      allRoles.value = (data?.roles ?? []) as unknown as UserRole[]
    }

    async function updateAreaType(
      posting: Ref<boolean>,
      id: string,
      body: { name: string },
    ) {
      const { data } = await withLoading(posting, () =>
        v1AreaTypeControllerUpdate({
          body,
          path: { id },
          throwOnError: true,
        }))
      return data
    }

    function fetchAllAdministeredWorkspaces(
      fetching: Ref<boolean>,
      overview?: false,
    ): Promise<Workspace[]>
    function fetchAllAdministeredWorkspaces(
      fetching: Ref<boolean>,
      overview: true,
    ): Promise<WorkspaceOverview[]>
    async function fetchAllAdministeredWorkspaces(
      fetching: Ref<boolean>,
      overview: boolean = false,
    ): Promise<Workspace[] | WorkspaceOverview[]> {
      const { data } = await withLoading(fetching, () =>
        overview
          ? v1WorkspacesControllerFindAllOverview({ throwOnError: true })
          : v1WorkspacesControllerFindAll({ throwOnError: true }))
      const workspaceData = (data?.workspaces ?? []) as unknown as Workspace[]
      if (userRoles.value.includes(UserRolesEnum.GLOBAL_ADMIN)) {
        const globalWorkspace = {
          name: 'Global',
          members: [],
          administrators: [],
          riskValueChains: [],
          riskValueChainPool: [],
          solutions: [],
        } as unknown as Workspace
        workspaceData.push(globalWorkspace)
      }
      if (!overview) {
        allAdministeredWorkspaces.value = [...workspaceData]
      }
      return workspaceData
    }

    async function createWorkspace(
      posting: Ref<boolean>,
      body: CreateWorkspaceRequest,
    ) {
      const { data } = await withLoading(posting, () =>
        v1WorkspacesControllerCreate({
          body,
          headers: adminApiHeaders,
          throwOnError: true,
        }))
      return data as unknown as Workspace
    }

    async function deleteWorkspace(deleting: Ref<boolean>, id: string) {
      if (id) {
        try {
          await withLoading(deleting, async () => {
            const { data } = await v1WorkspacesControllerDelete({
              headers: adminApiHeaders,
              path: { id },
              throwOnError: true,
            })
            return data
          })
          allAdministeredWorkspaces.value
            = allAdministeredWorkspaces.value.filter(
              workspace => workspace.id !== id,
            )
          showNotification.value = true
        }
        catch {
          showNotification.value = true
          customError.value = 'workspace_deleting_error'
        }
      }
    }

    async function addWorkspaceAdmin(
      posting: Ref<boolean>,
      workspaceId: string,
      userId: string,
    ) {
      const { data } = await withLoading(posting, () =>
        v1WorkspacesControllerAddAdministrator({
          body: { userId },
          headers: adminApiHeaders,
          path: { id: workspaceId },
          throwOnError: true,
        }))
      return data as unknown as Workspace
    }

    async function removeWorkspaceAdmin(
      deleting: Ref<boolean>,
      workspaceId: string,
      userId: string,
    ) {
      const { data } = await withLoading(deleting, () =>
        v1WorkspacesControllerRemoveAdministrator({
          headers: adminApiHeaders,
          path: { id: workspaceId, userId },
          throwOnError: true,
        }))
      return data as unknown as Workspace
    }

    async function updateWorkspaceRsv(
      posting: Ref<boolean>,
      workspaceId: string,
      rsvIds: string[],
    ) {
      const body = {
        riskValueChainIds: rsvIds,
      }
      const { data } = await withLoading(posting, () =>
        v1WorkspacesControllerUpdateRiskValueChainAssociations({
          body,
          path: { id: workspaceId },
          throwOnError: true,
        }))
      return data as unknown as Workspace
    }

    async function updateWorkspaceMembers(
      posting: Ref<boolean>,
      workspaceId: string,
      memberIds: string[],
    ) {
      const body = { userIds: memberIds }
      const { data } = await withLoading(posting, () =>
        v1WorkspacesControllerUpdateMember({
          body,
          path: { id: workspaceId },
          throwOnError: true,
        }))
      return data as unknown as Workspace
    }

    async function fetchAvailableSolutionTypes(
      fetching: Ref<boolean>,
      workspaceId?: string,
    ) {
      const { data: responseData } = await withLoading(fetching, () =>
        v1WorkspacesControllerGetWorkspaceAvailableSolutionTypes({
          path: { id: toWorkspacePathId(workspaceId) },
          throwOnError: true,
        }))
      const data = (responseData?.solutionTypes ?? []).map(normalizeSolutionType)
      const solutionTypeIds = data.map(st => st.id)
      if (solutionTypeIds.length > 0) {
        const imagesData = await getBatchSolutionImages(
          fetching,
          solutionTypeIds,
        )
        // Map the solution types with their images
        availableSolutionTypes.value[workspaceId ?? 'global'] = data.map(
          solutionType => ({
            ...solutionType,
            images: imagesData[solutionType.id] ?? [],
          }),
        )
      }
    }

    async function fetchAssociatedSolutionTypes(
      fetching: Ref<boolean>,
      workspaceId: string,
    ) {
      const { data: responseData } = await withLoading(fetching, () =>
        v1WorkspacesControllerGetWorkspaceAssociatedSolutionTypes({
          path: { id: workspaceId },
          throwOnError: true,
        }))
      const data = (responseData?.solutionTypes ?? []).map(normalizeSolutionType)
      const solutionTypeIds = data.map(st => st.id)
      if (solutionTypeIds.length > 0) {
        const imagesData = await getBatchSolutionImages(
          fetching,
          solutionTypeIds,
        )
        // Map the solution types with their images
        associatedSolutionTypes.value[workspaceId] = data.map(
          solutionType => ({
            ...solutionType,
            images: imagesData[solutionType.id] ?? [],
          }),
        )
      }
    }

    async function updateAssociatedSolutionTypes(
      posting: Ref<boolean>,
      workspaceId: string,
      solutionTypeIds: string[],
    ) {
      const body = {
        solutionTypeIds,
      }
      const { data } = await withLoading(posting, () =>
        v1WorkspacesControllerUpdateSolutionTypeAssociations({
          body,
          path: { id: workspaceId },
          throwOnError: true,
        }))
      return data as unknown as Workspace
    }

    async function updateWorkspace(
      posting: Ref<boolean>,
      id: string,
      body: { name: string },
    ) {
      const { data } = await withLoading(posting, () =>
        v1WorkspacesControllerUpdate({
          body,
          path: { id },
          throwOnError: true,
        }))
      return data as unknown as Workspace
    }

    async function duplicateRiskValueChain(
      posting: Ref<boolean>,
      riskValueChainId: string,
      targetWorkspaceId: string,
      newName: string,
    ) {
      const { data } = await withLoading(posting, () =>
        v1RiskValueChainControllerDuplicate({
          body: { targetWorkspaceId, name: newName },
          headers: adminApiHeaders,
          path: { riskValueChainId },
          throwOnError: true,
        }))
      const result = data as unknown as RiskValueChain
      if (result) {
        showNotification.value = true
        // If duplicated to current workspace, refresh the list
        if (selectedWorkspace.value?.id === targetWorkspaceId) {
          await fetchWorkspaceAvailableRiskValueChains(posting)
        }
      }
      return result
    }

    return {
      createRiskValueChain,
      fetchAllAreaTypes,
      allAreaTypes,
      fetchDefinitionSets,
      definitionSets,
      linkAreaTypesToRiskValueChain,
      unlinkAreaTypesFromRiskValueChain,
      deleteRiskValueChain,
      createSolutionType,
      createDefinitionSet,
      updateDefinitionSet,
      deleteDefinitionSet,
      fetchWorkspaceAvailableRiskValueChains,
      fetchGlobalRiskValueChains,
      fetchRiskValueChain,
      selectedRiskValueChain,
      selectedAreaType,
      fetchAreaType,
      selectedScenario,
      fetchScenario,
      allScenarioTypes,
      fetchAllScenarioTypes,
      deleteAreaType,
      createAreaType,
      riskValueChains,
      allRiskTypes,
      fetchAllRiskTypes,
      createRiskType,
      createScenarioType,
      deleteScenarioType,
      updateScenarioType,
      updateRiskValueChain,
      fetchRiskTypesRecommendations,
      fetchAllRiskTypesRecommendations,
      linkRiskTypesToAreaType,
      unlinkRiskTypesFromAreaType,
      shouldRefresh,
      linkScenariosToRiskType,
      unlinkScenariosFromRiskType,
      linkSolutionsToScenario,
      unlinkSolutionsFromScenario,
      fetchUsers,
      managedUsers,
      updateUserAccess,
      fetchAllRoles,
      allRoles,
      updateAreaType,
      fetchAllAdministeredWorkspaces,
      allAdministeredWorkspaces,
      createWorkspace,
      deleteWorkspace,
      addWorkspaceAdmin,
      removeWorkspaceAdmin,
      updateWorkspaceRsv,
      selectedWorkspace,
      updateWorkspaceMembers,
      globalRiskValueChains,
      deleteSolutionType,
      updateSolutionType,
      selectedRiskTypeRecommendation,
      availableSolutionTypes,
      associatedSolutionTypes,
      fetchAvailableSolutionTypes,
      fetchAssociatedSolutionTypes,
      updateAssociatedSolutionTypes,
      updateWorkspace,
      duplicateRiskValueChain,
    }
  },
  {
    persist: {
      key: 'admin-selected-workspace',
      storage: piniaPluginPersistedstate.localStorage(),
      pick: ['selectedWorkspace'],
    },
  },
)
